import { differenceInMinutes, fromUnixTime } from "date-fns";

import { SESSION_EXPIRED_EVENT } from "@/constants/settings";

import { refreshToken } from "@/api/refreshToken";

import { getSdpTenantName } from "@/helpers/getSdpTenantName";
import { keycloakAuthState } from "@/helpers/keycloakAuthState";
import { localStorageSessionToken } from "@/helpers/localStorageSessionToken";
import { normalizeApiError } from "@/helpers/normalizeApiError";
import { parseJwt } from "@/helpers/parseJwt";

import { AnyObject } from "@/types";

type FetchApiOptions = {
  withoutAuth?: boolean;
  omitContentType?: boolean;
  customCallback?: (request: Response) => void;
  organizationName?: string;
};

export const fetchApi = async (
  fetchUrl: string,
  fetchOptions?: RequestInit,
  options?: FetchApiOptions,
) => {
  const config = { ...fetchOptions };

  if (keycloakAuthState.isKeycloakMode) {
    // In Keycloak mode: Istio handles authentication via cookies/headers.
    // No local token management needed.
    config.credentials = "include";
    config.headers = {
      ...config.headers,
      "SDP-Tenant-Name": getSdpTenantName(options?.organizationName),
      ...(!options?.omitContentType
        ? {
            "Content-Type": (config?.headers as AnyObject)?.["Content-Type"] ?? "application/json",
          }
        : {}),
    };
  } else if (!options?.withoutAuth) {
    // Legacy mode: Handle session token if auth is required
    let token = localStorageSessionToken.get();

    // No need to continue if there is no token
    if (!token) {
      return;
    }

    const jwt = parseJwt(token);
    // JWT session is 15 min
    const minRemaining = differenceInMinutes(fromUnixTime(jwt.exp), Date.now());

    if (minRemaining <= 0) {
      sessionExpired();
      return;
    } else if (minRemaining < 5) {
      try {
        token = await refreshToken(token);
        localStorageSessionToken.set(token);
      } catch {
        // Token refresh failed (network error, 401, etc.) — treat as session expired
        sessionExpired();
        return;
      }
    }

    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
      "SDP-Tenant-Name": getSdpTenantName(options?.organizationName),
      ...(!options?.omitContentType
        ? {
            "Content-Type": (config?.headers as AnyObject)?.["Content-Type"] ?? "application/json",
          }
        : {}),
    };
  }

  // Handle initial request
  const request = await fetch(fetchUrl, config);

  if (options?.customCallback) {
    return options.customCallback(request);
  }

  if (request.status === 401) {
    if (keycloakAuthState.isKeycloakMode) {
      // In Keycloak mode: reload page so Istio redirects to Keycloak login
      window.location.reload();
      return;
    }
    sessionExpired();
    return;
  }

  // For any other non-2xx response, parse body and throw with HTTP status
  if (!request.ok) {
    let errorBody: AnyObject = {};
    try {
      errorBody = await request.json();
    } catch {
      // body is not JSON
    }
    const appError = normalizeApiError(errorBody);
    appError.status = request.status;
    throw appError;
  }

  const response = await request.json();

  if (response?.error) {
    const appError = normalizeApiError(response);
    appError.status = request.status;
    throw appError;
  }

  return response;
};

function sessionExpired() {
  document.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  return;
}
